import Vue from 'vue';
import VueRouter from 'vue-router';
import { permissionGuard, pendingPermissionChecks, checkPermissionAccess } from './permissionGuard';
import { EventBus } from '@/eventBus';
import { FEATURES } from '@/config/featureConfig';
import { ATLAS_SETTINGS_LINKS, isAtlasSettingsEnabled } from '@/config/atlasSettingsLinks';
import CallbackPage from '../views/CallbackPage.vue';
import SlackCallbackPage from '../views/SlackCallbackPage.vue';
import Settings from '../views/Settings.vue';
import Onboard from '../views/Onboard.vue';
import OnboardContact from '../views/OnboardContact.vue';
import OnboardMessaging from '../views/OnboardMessaging.vue';
import OnboardReport from '../views/OnboardReport.vue';
import OnboardPurpose from '../views/OnboardPurpose.vue';
import OnboardPricing from '../views/OnboardPricing.vue';
import Notification from '../views/Notification.vue';
import DataFileView from '../views/DataFileView.vue';
import SlackSuccessPage from '../views/SlackSuccessPage.vue';
import CustomReports from '../views/CustomReports.vue';
import CustomDashboards from '../views/CustomDashboards.vue';
import TemplateReports from '../views/TemplateReports.vue';
import TemplateReportPage from '../views/TemplateReport.vue';
import Subscription from '../views/Subscription.vue';
import SubscriptionConfirm from '../views/SubscriptionConfirm.vue';
import Dashboard from '../views/SystemDashboard.vue';
import MyDashboard from '../views/MyDashboard.vue';
import Users from '../views/Users.vue';
import DownloadManager from '../views/DownloadManager.vue';
import CardDesign from '../views/CardDesign.vue';
import ReportSelect from '../views/ReportSelect.vue';
import StatusPage from '../views/StatusPage.vue';
import DetailedDashboard from '../views/DetailedDashboard.vue';
import Home from '../views/Home.vue';
import LiveDemo from '../views/LiveDemo.vue';
import AutoSubscription from '../views/AutoSubscription.vue';
import OrderConfirm from '../views/OrderConfirm.vue';
import Plugins from '../views/Plugins.vue';
import Plugin from '../views/Plugin.vue';
import Oauth2Callback from '../views/Oauth2Callback.vue';
import ConsentScreen from '../views/oauth/ConsentScreen.vue';
import ProductSegmentDetail from '../views/ProductSegmentDetail.vue';
import ProductSegmentListing from '../views/ProductSegmentListing.vue';
import EtlStatusPage from '../views/EtlStatusPage.vue';
import ScheduleProfiles from '../views/ScheduleProfiles.vue';
import ScheduleProfile from '../views/ScheduleProfile.vue';
import Schedules from '../views/Schedules.vue';
import Schedule from '../views/Schedule.vue';
import ScheduleDetail from '../views/ScheduleDetail.vue';
import ProductSegmentExplore from '../views/ProductSegmentExplore.vue';
import ScheduleTemplateExplore from '../views/ScheduleTemplateExplore.vue';
import DashboardLibrary from '../views/DashboardLibrary.vue';
import ReportLibrary from '../views/ReportLibrary.vue';
import PremiumOnboarding from '../views/PremiumOnboarding.vue';
import QuickbooksOauth2Callback from '@/views/QuickbooksOauth2Callback.vue';
import SetupGuide from '../views/SetupGuide.vue';
import EmailTemplates from '../views/EmailTemplates.vue';
import EmailTemplate from '../views/EmailTemplate.vue';
import EmailTemplateNew from '../views/EmailTemplateNew.vue';
import EmailReceivers from '../views/EmailReceivers.vue';
import SpreadsheetTemplates from '../views/SpreadsheetTemplates.vue';
import SpreadsheetConnected from '../views/SpreadsheetConnected.vue';
import SpreadsheetClone from '../views/SpreadsheetClone.vue';
import SpreadsheetIntegration from '../views/SpreadsheetIntegration.vue';
import QuickbooksIntegration from '../views/QuickbooksIntegration.vue';
import GettingStarted from '../views/GettingStarted.vue';
import StockySwitcher from '../views/StockySwitcher.vue';
import StockyDataMigration from '../views/migration/StockyDataMigration.vue';
import BuyingListDetail from '../views/buying-list/BuyingListDetail.vue';
import SupplierList from '../views/supplier/SupplierList.vue';
import SupplierDetail from '../views/supplier/SupplierDetail.vue';
import SupplierCreate from '../views/supplier/SupplierCreate.vue';
import BuyingLists from '../views/buying-list/BuyingLists.vue';
import BuyingListSuggestion from '../views/buying-list/BuyingListSuggestion.vue';
import BuyingTable from '../views/inventory/BuyingTable.vue';
import GoogleSpreadsheetTemplates from '../views/GoogleSpreadsheetTemplates.vue';
import GoogleSpreadsheetTemplateDetail from '../views/GoogleSpreadsheetTemplateDetail.vue';
import GoogleSpreadsheetFiles from '../views/GoogleSpreadsheetFiles.vue';
import GoogleSpreadsheetFileDetail from '../views/GoogleSpreadsheetFileDetail.vue';
import GoogleSpreadsheetIntroductionView from '../views/GoogleSpreadsheetIntroduction.vue';
import InventoryTransferSuggestions from '../views/InventoryTransferSuggestions.vue';
// import InventoryTransferManagement from '../views/InventoryTransferManagement.vue';
import PermissionInitError from '../views/PermissionInitError.vue';
import SupplierImportUpload from '../views/supplier/SupplierImportUpload.vue';
import PageNotFound from '../views/PageNotFound.vue';
import AutomationDashboard from '../views/automation-dashboard/index.vue';
import UnapprovedPOs from '../views/purchase-order/UnapprovedPOs.vue';
import PermissionDenied from '../views/PermissionDenied.vue';
import PurchaseOrderList from '../views/purchase-order/PurchaseOrderList.vue';
import PurchaseOrderCreate from '../views/purchase-order/PurchaseOrderCreate.vue';
import PurchaseOrderDetail from '../views/purchase-order/PurchaseOrderDetail.vue';
import AutomatedPOConfig from '../views/purchase-order/AutomatedPOConfig.vue';
import PODashboard from '../views/purchase-order/PODashboard.vue';
const DataImporting = () => import('../views/DataImporting.vue');
const CustomColumnValues = () => import('../views/settings/CustomColumnValues.vue');
import { insightsRoutes } from './insights';

// Lazy-loaded Shopify Flow views (code-split for smaller initial bundle)
const ShopifyFlowSettings = () => import('../views/shopify-flow/ShopifyFlowSettings.vue');
const ShopifyFlowEvents = () => import('../views/shopify-flow/ShopifyFlowEvents.vue');
const ShopifyFlowTemplates = () => import('../views/shopify-flow/ShopifyFlowTemplates.vue');

Vue.use(VueRouter);

const routes = [
  {
    path: '/automation-dashboard',
    name: 'automationDashboard',
    component: AutomationDashboard,
    meta: {
      requiredPermission: FEATURES.AUTOMATED_PURCHASE_ORDER,
    },
  },
  {
    path: '/shopify-flow',
    name: 'shopifyFlowSettings',
    component: ShopifyFlowSettings,
    meta: {
      requiredPermission: FEATURES.SHOPIFY_FLOW_INTEGRATION,
    },
  },
  {
    path: '/shopify-flow/events',
    name: 'shopifyFlowEvents',
    component: ShopifyFlowEvents,
    meta: {
      requiredPermission: FEATURES.SHOPIFY_FLOW_INTEGRATION,
    },
  },
  {
    path: '/shopify-flow/templates',
    name: 'shopifyFlowTemplates',
    component: ShopifyFlowTemplates,
    meta: {
      requiredPermission: FEATURES.SHOPIFY_FLOW_INTEGRATION,
    },
  },

  // Insights Hub routes (Professional Plan and above)
  ...insightsRoutes,
  {
    path: '/buying-calendar',
    name: 'buyingListSuggestion',
    component: BuyingListSuggestion,
    props: true,
    meta: {
      requiredPermission: FEATURES.OPERATIONAL_MODE,
    },
  },
  {
    path: '/slack/callback',
    name: 'slackCallback',
    component: SlackCallbackPage,
  },
  {
    path: '/shopify/callback',
    name: 'callback',
    component: CallbackPage,
  },
  {
    path: '/settings',
    name: 'settings',
    component: Settings,
  },
  {
    path: '/settings/:tab',
    name: 'settingTabs',
    component: Settings,
  },
  {
    path: '/settings/products/custom-column-values',
    name: 'custom-column-values',
    component: CustomColumnValues,
    meta: {
      title: 'Custom Column Values',
      requiredPermission: FEATURES.OPERATIONAL_MODE,
    },
  },
  {
    path: '/',
    name: 'home',
    component: Home,
    props: true,
  },
  {
    path: '/onboard',
    name: 'onboard',
    component: Onboard,
  },
  {
    path: '/onboard-contact',
    name: 'onboardContact',
    component: OnboardContact,
  },
  {
    path: '/onboard-messaging',
    name: 'onboardMessaging',
    component: OnboardMessaging,
  },
  {
    path: '/onboard-report',
    name: 'onboardReport',
    component: OnboardReport,
  },
  {
    path: '/onboard-purpose',
    name: 'onboardPurpose',
    component: OnboardPurpose,
  },
  {
    path: '/onboard-pricing',
    name: 'onboardPricing',
    component: OnboardPricing,
  },
  {
    path: '/subscription/confirm',
    name: 'subscriptionConfirm',
    component: SubscriptionConfirm,
  },
  {
    path: '/subscription',
    name: 'subscription',
    component: Subscription,
  },
  {
    path: '/files/:file_name',
    name: 'dataFileView',
    component: DataFileView,
  },
  {
    path: '/slack/success',
    name: 'slackSuccessPage',
    component: SlackSuccessPage,
  },
  {
    path: '/reports',
    name: 'templateReports',
    component: TemplateReports,
    props: true,
  },
  {
    path: '/report-library',
    name: 'reportLibrary',
    component: ReportLibrary,
    meta: {
      title: 'Report Library',
    },
  },
  {
    path: '/reports/measures/:measure_code',
    name: 'detailReportPage',
    component: TemplateReportPage,
    props: true,
  },
  {
    path: '/analytics/:measure_code/:report_name',
    name: 'detailReportByName',
    component: TemplateReportPage,
    props: true,
  },
  {
    path: '/reports/schedule/:preview',
    name: 'previewScheduledReport',
    component: TemplateReportPage,
    props: true,
  },
  {
    path: '/my-reports',
    name: 'customReports',
    component: CustomReports,
  },
  {
    path: '/ai-report-builder',
    redirect: '/assistant/ai-report-builder',
  },
  {
    path: '/purchase-assistant',
    name: 'purchaseAssistant',
    component: () => import('../views/PurchaseAssistant.vue'),
    meta: {
      requiredPermission: FEATURES.PAID_MEASURES,
    },
  },
  {
    path: '/my-dashboards',
    name: 'customDashboards',
    component: CustomDashboards,
    props: true,
  },
  {
    path: '/my-reports/:custom_report_id',
    name: 'customReport',
    component: TemplateReportPage,
  },
  {
    path: '/reports/:type_id/:type_name',
    name: 'templateReportsByTypes',
    component: TemplateReports,
    props: true,
  },
  {
    path: '/reports/:type_id/:type_name/:template_report_id',
    name: 'templateReportByType',
    component: TemplateReportPage,
    props: true,
  },
  {
    path: '/reports/:template_report_id',
    name: 'templateReport',
    component: TemplateReportPage,
    props: true,
  },
  {
    path: '/schedule',
    name: 'schedule',
    component: Notification,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedule/edit/:schedule_id/report/:report_index',
    name: 'scheduleEditReport',
    component: TemplateReportPage,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/card/view/:card_id',
    name: 'viewCardReport',
    component: TemplateReportPage,
    props: true,
  },
  // Route aliases for key reports - easier redirect paths
  {
    path: '/inventory-forecasting',
    redirect: {
      path: '/analytics/inventory_forecasting/forecast-future-demand',
      query: {
        report_query: 'eJzNWG1v2zYQ_iuCgAEtZnWyHOXtm-s4RbHFzWwnwxAEAiPRjlaJ1PiSzgjy33ekJJOWHUvuMmxfBIh87u65O77c8dkVqciwe-5eUoZjxIWzkEIy7CQ4RyRxe278iJiYrwoFmt1cXQ2nv8PoI-IjNeGeCyZxz01SXmRodUUThRPoAZT2XI4zHAucjBBLRjSTOeHu-d2zS1CuYLeIpYgIZaSUe1oPJEigyqjAf6mRTKb8ChVFSpbuOZFZ9tJbK1qTn6EMc-dXCUpSsTKK7xYVIuIKEf1ZIe43TSU4TnOUNa25r0i7FoU5FShzbkgquDOjWWLZlmow4jAYpeQJE0HZKoJoEQLaOzLYq8N9ue-5OUYcEgdYA6h5K4zKUY4JTykBzMMqMsHmAjIZiVQ7EvhB6PmnXv8EZjBJNsb7fa9_rFThBZKZiBgiSzU5yGFwkWYCM0gwsCnSJyoiWojSnCQgkRKswlIghvKNdbBO3xxMOe8StOLvTfzWwddEerBIMgnjd-7Ad--r3yp6HBzVC8-KKJH5A7CCgFKU6GAuUMaxlbph8ofksEqdOcPESlyZbFENrs32m1azlIvXbMLUrYKW_qaJe37ac39BDzgD2I993_8BULelajf44OslpWBnFuxsA9X_cLZG9X0LdtqAnRpY34KdNGAnBhZYsOMG7NjABhYsbMBCAzuyYEcN2JGBhRZs0IANDOzYggUNWGBgJxvh3YT1DczOwibKYKwUeJuqfCsFgZUCL2jATAoCKwXeoAEzKQisFHhHDZhJQWClwAsbMJOCwEqBd9yAmRQEVgq8kwbMpCCwUuCdNmAmBYGVAu-sAevrs-rVrTh7pN-cOS3MJhS0IPbuG2ZZl11fXxrb-y-0vAgtbiZqoeVA37fXhtmeobWAAhsTWBhrAYU2JjSYY2v1KNcMSP3tiNW9uVUjhgsKB3e8fbNeM5rI2LpZPwuc36mPc4F5zFJ9MN_vjtnGxQMXRVEps_L0Zjf37Ocbo6QyFPGv8iBmWuB_UA70XCQFnen0bK_tBqthTqUdwSYnpOcbjGLJ4DqKV22UUK28EyFdXDiquHAKzBy4fy1a6AkztMRRgtJsFVl1SG2yY9Ba9bSwnWDxX5VX-4nd7I_eUvv6D_m1qWmh-LkW2LUXhrGQULpuYzpye1W-hdQXKTy68Lig8VfnAmo-ixOVUDwuIj0XqXqwI5ctudbA7LafksNtb8i02FXmHLqwgjaFfiutUlmzMOq-f-Xs1dH1gPgCe1crwslPsAmT9d_es5WCVKQqf1YG5tDTtSnfle32SnQmGCcb6d3RIXVO8-7u6kBq-ppv8lq7bozoQufQyDXEWzdBTHPQsCuZaTV36BW5Jdc1QvOyyduKiGr-Dg1D1TC2Hd7OiHL7Iq5vKn3gxnTrhnv1Et4SbDEO45mK-zVLY9vpjfGuxpvK9hmuykPA0gX4f4XYMiUWgbquKvR8lOv5BhG462JMtsuynbId-cwEEtI6h9f1XT1-SE1Yy3QxrHVumRXl6AFGK4lOJtFyh6eiHD3EJGo7fW4xSSgztsp_Z4Lyjt491Qq2rainHrXky5ce6EGe9T90zz03hXaDX2N2rQdC6HQ4tCsfV6qNa6va7kuwalYArt4YYWTJqCy0fP1TzavnL8nFjLKamBrIRGqNvID_KItlhlTrlGMB-4SXsnBUFdC_cErgohTA9VmBaUbZVGZV61g2WaMuNRPHMSXVe-eoveGoW1u_F_hAhsLGQqBTLZXxdDSezIefxtHF58vL8XQ8GY2jj-P5b-PxxK0oAo4tH94FA7_nBGEIn4H_vp6EWFa5ZfSb2_5IqFbNv-6pejGxvPw0HQ_n42k0nFxoN6PK7aiaaDqqfTzy9ecNHd3De5ebe-Pyho76R_rz_Y7qx1hSk1dd4fng5W_uxgwH',
      },
    },
    meta: {
      requiredPermission: FEATURES.DEMAND_FORECASTING,
    },
  },
  {
    path: '/reorder-suggestions',
    redirect: { 
      path: '/analytics/inventory-replenishment/reorder-analysis',
      query: { 
        report_query: 'eJztWeFu2zYQfhVBwIAWswtLtuzEGAa0aQYUa9osSTMMQSAwEp1wpUSVpNIYQd59R8o2T7LsOTbQdoP_uBX53d3HO5J3xzz6mmlO_bF_RoVMqfRITvhUMeV3_OSOSH0xLcw0fN4RdWRG_LGWJe34KVMFJ9MTkRqAJjegp-MrymmiaXpEZHokeJnlyh9fPfo5yQzsVIq0TLTRXsm90zS7Mj_eW6oSyQrNRH4N8ynRZGZc0wcjwUumTkhRsPwWBm-mcTFT9tRZ6L8kkpEc6b9fDPybwjkSaTv__ZPTNLMWq8_ls-hZAaR07uk_SrDG9NRZuGL5Pc21kNNY0oLTnKm7DAYa7khpwjLCl0yukG6z_ZZoiuzKajgGI7RprELWLWE8Vv8pZ1p554KnSLmCz_jLbLFxQSTJNlxQi-SyNQPyClhTSrAryT2V5JYCRcancWmgsdX3HALrlWAur9O_SwXb3iu3IqUIpyrWkubprtSQKkzwPSWpd8Ey6r0ATuolYgW7JhUy5oCINSA2ZNAUqx0cMqF66p1rkXzGW8EOx8oMg-A95ZvuhCXBtl19Khg--ottXZjxDS3VZLCRd_PT1XZwXye6JNxbxmxodaV8nUEiMsC33xzVXOwugS_Po7BGAybxFjaPJyaI6hnNCMuNEhRoGyiz0ZA6yBa5wW0a83U6MKUTsJ6VWZv3HCNY2va-aReucSAPG3AgDztwaBVu84M9dd6RuLcX_VQ1vFD5NTHT1rvP8EFTtM0D66zDEra13iLatvaPq1KroV-d7C1iXxdsW_Vqu0B8O7tLgtjub-wBks1KqxMzvZ3dNtG2y_ZomvC2GiIx48-8bK1MM7XDHlL4Np9nPJPrYBeo5o2elBKSXjJdmSoXgv7TdcfPKFGlpGvqJlPiwn8UFKRVQedqSaWhEK6y3tgPe2HU7R10gxHMQFKsjQdBNxgaVXRCSq5jSfJbM9nPYHDCuKYS6mPgU7B7oWNh61-YL3OQYDk1lZStNmpl9KLcuLB5fuEknPw7UNHyEkav_MC_nn3NfMWZapTEeZndAJWOnbo00MogS_3xQcd_T24g5479n4Ne7ydAXVaq_fBVz8bNwA4R7LCGCl4dLlBBD8EOGrADBwsQbNSAjRwsRLBhAzZ0sD6CRQ1Y5GADBBs0YAMHixCs34D1HWyIYGEDFjrYqObeOixwMByFOsphUAi6dVU9FIIQhaAbNmAuBCEKQbffgLkQhCgE3UED5kIQohB0owbMhSBEIegOGzAXghCFoDtqwFwIQhSC7kED5kIQohB0DxuwwF4XXJDUXicTwhVFV9XxQ8JLM-WJUnfFpGvTlJfWUh9MxWISo5KGzsXwOc1F86AqQNhrtqXfXD6oQxSxDwKtAhTP1zpE4fqLKgSawtfapZ7fia_ehSjcurQocryA15zvtoIIBSxC3NwGiVCsgh4-Bu4mitBZCTEmRBh0ViKMiRxmiA6KWZoDma8WX127BxCTTIQ0CWfVI4j3LoO05JzJ7Cfy9_6x5AfouhbtzXLj_E27nP2z0f7ZaP9s9B98NvoBHm32LyT7F5L9C8n_6oXk0l6jTnv17X0wsxsVXNU9_C0fXZylj7hTahyBpVZpQ08tybVU0h78O4FFnhB5y3JkdF59FnY-zux8wzDk5aR6FqobbpWt7wdUVqH7r-HqWvWFLrLneH2djtWUziAz5iXdgIukGgqH2PZ3u3LCulZzqyK2AbVbKZSaBWFXalhX7U4RkiZE6S3LtclMfNdaba4HJEipxbntOFELWhip6iUR-s5H--2Pg47PoHVUp1Se2oEIulsFLeqbqWndV3cR1xXMtJwANH_8hxFwUVlYyfnHbN48rUJNey7knJIZ4JqhkSdwFuFJyYlplDOqJUtUJQv1DFTJTIm8ahPGjwYsuJBnJZ89FFQt9dGmXdjsdWLU8QUEi8A8iP3iz9SabXB78yKMoo4X9gb25-V8EjTMgiTFV3-TR2OzW7bjdzXqBIPrGsk3xxd_Hh9_aKU66Nmf70M1GHT6vY249g1NS7j_fbgCAUzz19Vxj6JdCNon_PxoxrGEoXH_6R9eEiEP',
      },
    },
    meta: {
      requiredPermission: FEATURES.PAID_MEASURES,
    },
  },
  {
    path: '/sales-forecast',
    redirect: { 
      path: '/reports/measures/sales_forecast',
      query: {
        dimension: 'by_variant',
        report_query: 'eJzlWltv2zYU_iuCnlYgD02CvvitTRAs6dpkcLqhCAqBlmibCEVqvDgVgvz3kRRliZE8SyXVAtpLUZ3r9x1eco6l51gggWG8iJcAQx5dUQZTwEV8EqdbwMR9WWiletwCfqEl8UIwCU9ivqVPVrAGmGsJxDAVMLsALLugWOaEx4uH55iAXMf4CzAEiIiuc7CBOj7NtBjZxwwIYLPVIiwR_wSKApFNSwqkoEuTap85pZiya5LB7w0YygRYaWYa7svJHsYdo5lMRQPgWsD8Qf8TXUKeMlQIRMk3F5GA30UH0KpMin0wT1S2OA2q3V5wDEZj6Ylh-fFLk98yS_ijHIDBtQ5Ti-g665QjQZmLhsh8BdmhmlTmgeDcmo0RnXZBUaM5Hb5WLY-w4M4OgTsbDe4sOLjzQ-DOR4M79wf3ATADprPjV3vF0F3feHhi+kKQiC4o794DiVSqJK1ULVypZAyStDxYr7af7_0AMVbBozuG0lbdTIbCyoZAcxw8Md0ytEEE4NegqJWPAtZx8gT3N0SbbWstBRUq-FMtPX6NvXIIAifSm6zBVAU322TAjnetPfH8vlR7vX0CtzwZePIaS08MF1QSwcqIrqNqKzVo0kqV0HVCa9UxXH0-ngivyQ4SQRXGewbSR52n6Z0qnSgT0eiOYex18i4jEYhIGHF7RzxtIYmoFLqwXND0sV3XyjaxtokyS2qb4wX-D-cwnaHTd9RX_MC-wzEPBMdk7AASlXR4j1p7-P5NhySjrMFTPUeftXYQnF0dIFB5lgIIyXsa11o-okR7n1BLBzY9yEQlHbN0IMgZxdpXdU89mFJHObTxcb18iyZXGPEtzKJLIHp2fFHrk6zSt1BaST_KjqMn0CvTcHTn5QruA9eKZG0VrwbJDKZqjsUdrK5TAIw2koX5p1S9IBJldAcZoq377UGgHCaFkSYF2lF1CBwoiQZSTfaJzjFkMB4b05_u-x1kYANfsW3RBLuNBfGPVQ5cmK5jqEZMbfSDe-jJWvwY5gPeASYAg_cPva--QsA6mx7rxS2VZtSu33v5A9SocGnreq-6_KyDUWjpKHzCxglSPAOqb8n3h2M8zD7XEOMe4FTfdH13nNVVGYdW03HyB3j29uxddANI9Jt7vbwZc71VQmWRNOFy1WRu3wwj5ZmhXQL7u-qeruH_mu8VXIXka8JNytfNMJrvJ8BC8jXhJuXrZhjN930RlK8JNylfN8MPrG8Zdn3Lyde39OF7I8PeV3Ly-0p63Vc3Eofliyfni73Or9wEPb863LTn18kwmu8SFiH5mnCT8nUzjOZ7q8b6gHxNuEn5uhlG8_1MdyH5mnCT8nUzjOZ7CdOQfE24Sfm6GY7z_XYS56oRlwz2_eyQqeyEI0rsj3XNW2i4BhKLhAGy0Z5KtEZYQBYvHlTIClT1ci7Wr3iUPSJQjyEFYCDnlVn9iUDCYKFw6R-QftpnAr_gG4DJX_D_lLf3Mx-5Zj5hzXygmvn8NPNxaebT0cyHoZnPPjMfdWY-2cx8kPk_zi2F6uztjKFGhmfzHC9OT2KkOnd-B9mdEbx7W3l_KO3Qof6v23r1ZEIp0YZRWdR682AN9HQkuVjS1nfNuZp7UEvyosoKcCox0JNMDgVDqZ1vUpqrcQdxSqp3zYvnFz0bkepzaPOxUbw4f_kXWygOvQ',
      },
    },
    meta: {
      requiredPermission: FEATURES.DEMAND_FORECASTING,
    },
  },
  {
    path: '/data-importing',
    name: 'dataImporting',
    component: DataImporting,
    props: true,
  },
  {
    path: '/stocky-switcher',
    name: 'StockySwitcher',
    component: StockySwitcher,
  },
  {
    path: '/stocky-data-migration',
    name: 'StockyDataMigration',
    component: StockyDataMigration,
  },
  {
    path: '*',
    redirect: '/',
  },
  {
    path: '/myboard/:dashboard_id',
    name: 'myDashboard',
    component: MyDashboard,
    meta: {
      requiredPermission: FEATURES.CUSTOM_DASHBOARD,
    },
  },
  {
    path: '/my-board/:dashboard_id',
    name: 'myBoard',
    component: MyDashboard,
    meta: {
      requiredPermission: FEATURES.CUSTOM_DASHBOARD,
    },
  },
  {
    path: '/users',
    name: 'users',
    component: Users,
    meta: {
      requiredPermission: FEATURES.USER_MANAGEMENT,
    },
  },
  {
    path: '/download-manager',
    name: 'downloadManager',
    component: DownloadManager,
  },
  {
    path: '/design-card/:id',
    name: 'cardDesign',
    component: CardDesign,
    meta: {
      requiredPermission: FEATURES.CUSTOM_DASHBOARD,
    },
  },
  {
    path: '/report-select',
    name: 'reportSelect',
    component: ReportSelect,
  },
  {
    path: '/status/:code',
    name: 'statusPage',
    component: StatusPage,
  },
  {
    path: '/public-board/:dashboard_id',
    name: 'publicBoard',
    component: Dashboard,
  },
  {
    path: '/board/:dashboard_id/:type',
    name: 'dashboardType',
    component: Dashboard,
  },
  {
    path: '/board/:dashboard_id',
    name: 'dashboard',
    component: Dashboard,
  },
  {
    path: '/dashboard/product',
    name: 'productDashboard',
    component: DetailedDashboard,
  },
  {
    path: '/dashboard/store',
    name: 'storeDashboard',
    component: DetailedDashboard,
  },
  {
    path: '/dashboard/vendor',
    name: 'vendorDashboard',
    component: DetailedDashboard,
  },
  {
    path: '/dashboard/collection',
    name: 'collectionDashboard',
    component: DetailedDashboard,
  },
  {
    path: '/live-demo',
    name: 'liveDemo',
    component: LiveDemo,
  },
  {
    path: '/premium-onboarding',
    name: 'PremiumOnboarding',
    component: PremiumOnboarding,
  },
  {
    path: '/plugins',
    name: 'plugins',
    component: Plugins,
  },
  {
    path: '/plugin/:pluginId',
    name: 'plugin',
    component: Plugin,
  },
  {
    path: '/auto_subscription',
    name: 'autoSubscription',
    component: AutoSubscription,
  },
  {
    path: '/order_confirm',
    name: 'orderConfirm',
    component: OrderConfirm,
  },
  {
    path: '/oauth2callback',
    name: 'oauth2Callback',
    component: Oauth2Callback,
  },
  {
    path: '/quickbooks/oauth2callback',
    name: 'quickbooks/oauth2callback',
    component: QuickbooksOauth2Callback,
  },
  {
    path: '/oauth/consent',
    name: 'consentScreen',
    component: ConsentScreen,
  },
  {
    path: '/product-segment/:segment_id/type/:type',
    name: 'productSegmentDetail',
    component: ProductSegmentDetail,
    props: true,
    meta: {
      requiredPermission: FEATURES.PRODUCT_SEGMENTATION,
    },
  },
  {
    path: '/product/explore',
    name: 'productExplore',
    component: ProductSegmentExplore,
    props: true,
    meta: {
      requiredPermission: FEATURES.PRODUCT_SEGMENTATION,
    },
  },
  {
    path: '/product-segment',
    name: 'productSegmentListing',
    component: ProductSegmentListing,
    props: true,
    meta: {
      requiredPermission: FEATURES.PRODUCT_SEGMENTATION,
    },
  },
  {
    path: '/status-page',
    name: 'etlStatusPage',
    component: EtlStatusPage,
    props: true,
  },
  {
    path: '/importing-status',
    name: 'etlStatusPage',
    component: EtlStatusPage,
    props: true,
  },
  {
    path: '/schedule/profiles',
    name: 'scheduleProfiles',
    component: ScheduleProfiles,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedule/profile/:profile_id',
    name: 'scheduleProfileEdit',
    component: ScheduleProfile,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedule/profile',
    name: 'scheduleProfileCreate',
    component: ScheduleProfile,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedules',
    name: 'scheduleListing',
    component: Schedules,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedule/edit/:schedule_id',
    name: 'scheduleEdit',
    component: Schedule,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedule/create',
    name: 'scheduleCreate',
    component: Schedule,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedule/templates',
    name: 'scheduleTemplateExplore',
    component: ScheduleTemplateExplore,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedule/:schedule_id',
    name: 'scheduleDetail',
    component: ScheduleDetail,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/library/dashboard',
    name: 'DashboardLibrary',
    component: DashboardLibrary,
    props: true,
  },
  {
    path: '/setup-guide/:step',
    name: 'SetupGuide',
    component: SetupGuide,
    props: true,
  },
  {
    path: '/email-templates',
    name: 'EmailTemplates',
    component: EmailTemplates,
    props: true,
    meta: {
      requiredPermission: FEATURES.EMAIL_TEMPLATES,
    },
  },
  {
    path: '/email-templates/new',
    name: 'EmailTemplateNew',
    component: EmailTemplateNew,
    props: true,
    meta: {
      requiredPermission: FEATURES.EMAIL_TEMPLATES,
    },
  },
  {
    path: '/email-templates/:template_id',
    name: 'EmailTemplate',
    component: EmailTemplate,
    props: true,
    meta: {
      requiredPermission: FEATURES.EMAIL_TEMPLATES,
    },
  },
  {
    path: '/email-receivers',
    name: 'EmailReceivers',
    component: EmailReceivers,
    props: true,
    meta: {
      requiredPermission: FEATURES.EMAIL_TEMPLATES,
    },
  },
  {
    path: '/spreadsheet-templates',
    name: 'SpreadsheetTemplates',
    component: SpreadsheetTemplates,
    props: true,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/spreadsheet-connected',
    name: 'SpreadsheetConnected',
    component: SpreadsheetConnected,
    props: true,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/spreadsheet-clone/:id',
    name: 'SpreadsheetClone',
    component: SpreadsheetClone,
    props: true,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/spreadsheet-integration',
    name: 'SpreadsheetIntegration',
    component: SpreadsheetIntegration,
    props: true,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/quickbooks-integration',
    name: 'QuickbooksIntegration',
    component: QuickbooksIntegration,
    props: true,
    meta: {
      requiredPermission: FEATURES.QUICKBOOKS_INTEGRATION,
    },
  },
  {
    path: '/getting-started',
    name: 'GettingStarted',
    component: GettingStarted,
    props: true,
  },

  {
    path: '/suppliers',
    name: 'suppliers',
    component: SupplierList,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/suppliers/import/upload',
    name: 'SupplierImportUpload',
    component: SupplierImportUpload,
    meta: {
      requiredPermission: FEATURES.SUPPLIER_MANAGEMENT,
    },
  },
  {
    path: '/suppliers/:id',
    name: 'supplier',
    component: SupplierDetail,
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/supplier/create',
    name: 'supplierCreate',
    component: SupplierCreate,
    meta: {
      requiredPermission: FEATURES.PAID_MEASURES,
    },
  },
  {
    path: '/suppliers/:supplierId/purchase-orders/create',
    name: 'createPurchaseOrder',
    component: PurchaseOrderCreate,
    props: true,
    meta: {
      requiredPermission: FEATURES.OPERATIONAL_MODE,
    },
  },
  {
    path: '/buying-lists/:id',
    name: 'buyingListDetail',
    component: BuyingListDetail,
    props: true,
    meta: {
      requiredPermission: FEATURES.OPERATIONAL_MODE,
    },
  },
  {
    path: '/buying-lists',
    name: 'buying-lists',
    component: BuyingLists,
    meta: {
      requiresAuth: true,
      requiredPermission: FEATURES.OPERATIONAL_MODE,
      title: 'Buying Lists',
    },
  },
  {
    path: '/buying-table',
    name: 'buyingTable',
    component: BuyingTable,
    meta: {
      requiresAuth: true,
      requiredPermission: FEATURES.OPERATIONAL_MODE,
      title: 'Buying Table',
    },
  },
  {
    path: '/inventory-transfers/suggestions',
    name: 'inventoryTransferSuggestions',
    component: InventoryTransferSuggestions,
    meta: {
      requiresAuth: true,
      requiredPermission: FEATURES.REPLENISH_FROM_WAREHOUSE,
      title: 'Inventory Transfer Suggestions',
    },
  },
  // {
  //   path: '/inventory-transfers',
  //   name: 'inventoryTransferManagement',
  //   component: InventoryTransferManagement,
  //   meta: {
  //     requiresAuth: true,
  //     requiredPermission: FEATURES.REPLENISH_FROM_WAREHOUSE,
  //     title: 'Transfer Management',
  //   },
  // },
  {
    path: '/google-spreadsheet-introduction',
    name: 'GoogleSpreadsheetIntroduction',
    component: GoogleSpreadsheetIntroductionView,
  },
  {
    path: '/google-spreadsheet-templates',
    name: 'GoogleSpreadsheetTemplates',
    component: GoogleSpreadsheetTemplates,
    props: true,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/google-spreadsheet-templates/:id',
    name: 'GoogleSpreadsheetTemplateDetail',
    component: GoogleSpreadsheetTemplateDetail,
    props: true,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/google-spreadsheet-files',
    name: 'GoogleSpreadsheetFiles',
    component: GoogleSpreadsheetFiles,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/google-spreadsheet-files/:id',
    name: 'GoogleSpreadsheetFileDetail',
    component: GoogleSpreadsheetFileDetail,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/purchase-orders/dashboard',
    name: 'po-dashboard',
    component: PODashboard,
    meta: {
      requiredPermission: FEATURES.OPERATIONAL_MODE,
      title: 'Purchase Order Dashboard',
    },
  },
  {
    path: '/purchase-orders',
    name: 'purchase-orders',
    component: PurchaseOrderList,
    meta: {
      requiredPermission: FEATURES.OPERATIONAL_MODE,
      title: 'Purchase Orders',
    },
  },
  {
    path: '/purchase-orders/unapproved',
    name: 'unapproved-pos',
    component: UnapprovedPOs,
    meta: {
      title: 'Unapproved Purchase Orders',
      requiredPermission: FEATURES.AUTOMATED_PURCHASE_ORDER,
    },
  },
  {
    path: '/purchase-orders/:id',
    name: 'purchase-order-detail',
    component: PurchaseOrderDetail,
    props: true,
    meta: {
      requiredPermission: FEATURES.OPERATIONAL_MODE,
      title: 'Purchase Order Details',
    },
  },
  {
    path: '/automation-configs',
    name: 'AutomatedPOConfig',
    component: AutomatedPOConfig,
    meta: {
      requiredPermission: FEATURES.AUTOMATED_PURCHASE_ORDER,
    },
  },
  {
    path: '/automated-po-overview',
    name: 'automated-po-overview',
    component: () => import('@/views/automation-overview/AutomatedPOOverview.vue'),
    meta: {
      title: 'Automated Purchase Orders - Get Started',
      requiredPermission: FEATURES.AUTOMATED_PURCHASE_ORDER,
    },
  },
  // {
  //   path: '/purchase-order/automation/:id',
  //   name: 'AutomatedPOConfigDetail',
  //   component: AutomatedPOConfigDetail,
  //   props: true,
  //   meta: {
  //     permissions: ['purchase_order_write'],
  //   },
  // },
  // {
  //   path: '/purchase-order/automation/create',
  //   name: 'AutomatedPOConfigCreate',
  //   component: AutomatedPOConfigCreate,
  //   props: true,
  //   meta: {
  //     permissions: ['purchase_order_write'],
  //   },
  // },
  {
    path: '/premium_reports/:premium/:type_id/:type_name',
    name: 'premiumTemplateReportsByTypes',
    component: TemplateReports,
  },
  {
    path: '/permission-denied',
    name: 'permission-denied',
    component: PermissionDenied,
    meta: {
      title: 'Permission Denied',
    },
  },
  {
    path: '/permission-init-error',
    name: 'permissionInitError',
    component: PermissionInitError,
    meta: {
      title: 'Permission Initialization Error',
    },
  },
  {
    path: '/purchase-orders/create',
    name: 'createPurchaseOrder',
    component: PurchaseOrderCreate,
    props: true,
    meta: {
      // Add relevant permissions if needed
      // requiredPermission: FEATURES.PURCHASE_ORDER_MANAGEMENT
    },
  },
  {
    path: '/suppliers/actions/import',
    name: 'supplierImport',
    component: SupplierImportUpload,
    props: true,
    meta: {
      // Add relevant permissions if needed, e.g., for managing suppliers
      // requiredPermission: FEATURES.SUPPLIER_MANAGEMENT
    },
  },
  // Atlas AI Agent Platform routes
  {
    path: '/assistant',
    name: 'atlasHome',
    component: () => import('../views/AtlasEmbed.vue'),
    meta: {
      title: 'Atlas AI Agent',
    },
  },
  {
    path: '/assistant/setup/:tab?',
    name: 'atlasSetupTab',
    component: () => import('../views/AtlasEmbed.vue'),
    props: (route: any) => ({ agent: 'setup', tab: route.params.tab }),
    meta: {
      title: 'Atlas Setup',
    },
  },
  {
    path: '/assistant/:agent',
    name: 'atlasAgent',
    component: () => import('../views/AtlasEmbed.vue'),
    props: true,
    meta: {
      title: 'Atlas AI Agent',
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

// Use $once to ensure the listener runs only once
EventBus.$once('permissions-ready', () => {

  // Copy the array to avoid issues if it's modified during iteration
  const checksToProcess = [...pendingPermissionChecks];
  const currentRoute = router.currentRoute; // Get current route once


  let needsRedirect = false;
  let redirectInfo = { feature: '', fullPath: '' };

  checksToProcess.forEach((pendingCheck) => {

    const hasPermission = checkPermissionAccess(pendingCheck.requiredPermission, pendingCheck.to);

    if (!hasPermission) {

      // Check if this denied route is the one the user is currently on
      if (currentRoute.path === pendingCheck.to.path || currentRoute.path === pendingCheck.to.path.split('?')[0]) {
          needsRedirect = true;
          redirectInfo = {
              feature: pendingCheck.requiredPermission,
              fullPath: pendingCheck.to.fullPath,
          };
      }

      // Emit event even if it's not the current route (useful for logging/debugging)
      EventBus.$emit('permission-denied', {
        feature: pendingCheck.requiredPermission,
        route: pendingCheck.to,
      });
    }
  });

  // Clear the original pending checks array *after* processing all checks
  pendingPermissionChecks.length = 0;

  // Perform redirect *after* the loop if necessary
  if (needsRedirect) {
      router.replace({
          path: '/permission-denied',
          query: {
              feature: redirectInfo.feature,
              redirect: redirectInfo.fullPath,
          },
      });
  }
});

// Listen for permissions-failed event and redirect to error page
EventBus.$once('permissions-failed', () => {
  router.replace({ path: '/permission-init-error' });
});

// Redirect settings routes to Atlas Setup when atlas mode is enabled
router.beforeEach((to, from, next) => {
  const atlasTarget = ATLAS_SETTINGS_LINKS[to.path];
  if (isAtlasSettingsEnabled() && atlasTarget) {
    next({
      path: atlasTarget,
      query: to.query,
      hash: to.hash,
    });
    return;
  }
  next();
});

router.beforeEach(permissionGuard);

export default router;
